"use client";

import {
  type ReactNode,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type ResolutionRunState = {
  /** Section on screen and motion allowed: keyframes only run then. */
  readonly running: boolean;
  /** Bumps when the scene leaves below the viewport; keys the scene so the next pass replays. */
  readonly run: number;
  readonly setInView: (inView: boolean) => void;
  readonly rewind: () => void;
};

/** Fallback without provider: static end state, nothing runs. */
const STATIC_STATE: ResolutionRunState = {
  running: false,
  run: 0,
  setInView: () => undefined,
  rewind: () => undefined,
};

const ResolutionRunContext = createContext<ResolutionRunState>(STATIC_STATE);

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const onChange = (): void => setReduced(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/** Holds whether the scene is on screen; desktop canvas and phone stack share it. */
export function ResolutionModeProvider(props: { readonly children: ReactNode }): ReactNode {
  const [inView, setInView] = useState(false);
  const [run, setRun] = useState(0);
  const started = useRef(false);
  const reduced = usePrefersReducedMotion();
  const report = useCallback((next: boolean): void => {
    if (next) started.current = true;
    setInView(next);
  }, []);
  // Only after a run has begun: an untouched scene below the fold must not remount.
  const rewind = useCallback((): void => {
    if (!started.current) return;
    started.current = false;
    setRun((current) => current + 1);
  }, []);
  const value = useMemo(
    (): ResolutionRunState => ({ running: inView && !reduced, run, setInView: report, rewind }),
    [inView, reduced, run, report, rewind],
  );
  return (
    <ResolutionRunContext.Provider value={value}>{props.children}</ResolutionRunContext.Provider>
  );
}

export function useResolutionMode(): ResolutionRunState {
  return useContext(ResolutionRunContext);
}

/**
 * Reports whether the scene is on screen; the first sighting starts the run. Leaving
 * downward (scrolled back above it) rewinds; leaving upward keeps the finished state.
 */
export function useReportInView(
  ref: RefObject<HTMLElement | null>,
  /** Share that must be visible before the run starts. */
  threshold = 0.3,
): void {
  const { setInView, rewind } = useResolutionMode();
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            setInView(false);
            if (entry.boundingClientRect.top > 0) rewind();
          } else if (entry.intersectionRatio >= threshold) {
            setInView(true);
          }
        }
      },
      // Fine steps re-check while scrolling in; 0 catches leaving completely.
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      setInView(false);
    };
  }, [ref, setInView, rewind, threshold]);
}
